import React from 'react';
import { m } from 'motion/react';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldCheck, Zap, Calculator, CheckCircle2, ExternalLink, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { GOOGLE_REVIEWS_URL } from './GoogleReviews';

/** Official multicolour Google "G", inline so nothing loads from Google. */
const GoogleG: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const Hero: React.FC = () => {
  const { t, language } = useLanguage();
  const ht = t.hero;
  // Same-page anchor on Home — must carry the current language prefix, not
  // the bare "/" (see Navbar.tsx's navLinks for the same fix and why).
  const homePrefix = language === 'EN' ? '/en/' : '/de/';

  return (
    <section className="relative min-h-screen flex items-center pt-16 bg-pstax-bg w-full">
      {/* Background Decor */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-pstax-blue/5 rounded-full" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-pstax-blue/5 rounded-full" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <m.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pstax-blue/10 border border-pstax-blue/20 text-pstax-navy text-sm font-semibold mb-8">
              <ShieldCheck size={16} className="text-pstax-blue" />
              <span>{ht.badge}</span>
            </div>

            {/* Subtle Mobile Intro Portrait */}
            <div className="lg:hidden flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-lg ring-4 ring-pstax-blue/5">
                <img
                  src="/pascal-hero-240.webp"
                  alt="Pascal Schraml"
                  className="w-full h-full object-cover scale-110"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-pstax-blue mb-0.5">{ht.advisorLabel}</span>
                <span className="text-sm font-display font-bold text-pstax-navy tracking-tight">Pascal Schraml</span>
              </div>
            </div>
            
            <h1 className="text-[2.25rem] sm:text-5xl lg:text-5xl xl:text-6xl font-display font-bold leading-[1.1] mb-8 tracking-tight text-pstax-navy max-w-[16ch] lg:max-w-none xl:max-w-[18ch]">
              {ht.headline}
            </h1>
            
            <p className="text-lg text-pstax-gray mb-10 max-w-xl leading-relaxed">
              {ht.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`${homePrefix}#fit-check`} className="btn-primary flex items-center justify-center gap-2 group" data-cta-location="hero">
                {ht.ctaFit}
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={`${homePrefix}#calculator`} className="btn-outline flex items-center justify-center gap-2">
                <Calculator size={18} />
                {ht.ctaCalc}
              </Link>
            </div>

            {language === 'EN' && (
              <a
                href="https://setupinaustria.at/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-pstax-orange text-pstax-orange font-semibold px-6 py-3 text-sm hover:bg-pstax-orange/10 transition-all active:scale-95 w-fit"
              >
                {ht.partnerCta}
                <ExternalLink size={14} />
              </a>
            )}

            <div className="mt-8 flex items-center gap-3">
              <div className="flex -space-x-2">
                {['/melina.webp', '/rainer.webp', '/rory.webp'].map((img, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-pstax-blue overflow-hidden shadow-sm">
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium text-pstax-navy/70">
                <span className="font-bold text-pstax-navy">{ht.trusted}</span> {ht.trustedSub}
              </p>
            </div>

            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-full bg-white border border-pstax-border text-pstax-navy text-xs sm:text-sm font-semibold shadow-sm hover:border-pstax-blue/50 transition-colors"
            >
              <GoogleG />
              <span className="flex shrink-0 text-[#FBBC04]" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((n) => (
                  <Star key={n} size={12} fill="currentColor" strokeWidth={0} />
                ))}
              </span>
              <span className="sm:whitespace-nowrap">{ht.googleBadge}</span>
            </a>
            
            <div className="mt-16 flex flex-wrap items-center gap-4 lg:gap-8 text-pstax-gray font-medium">
              {ht.bullets.map((bullet: string, i: number) => (
                <div key={i} className="flex items-center gap-2 whitespace-nowrap">
                  <CheckCircle2 size={18} className="text-pstax-blue" />
                  <span className="text-sm">{bullet}</span>
                </div>
              ))}
            </div>
          </m.div>
          
          <div className="relative">
            <div className="relative z-10 rounded-[40px] overflow-hidden border border-pstax-border shadow-2xl aspect-[4/5] lg:aspect-auto">
              <img 
                src="/pascal-hero-768.webp"
                srcSet="
                  /pascal-hero-480.webp 480w,
                  /pascal-hero-768.webp 768w,
                  /pascal-hero-1200.webp 1200w,
                  /pascal-hero-1600.webp 1600w
                "
                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 600px"
                alt="Pascal Schraml"
                width="1200"
                height="1150"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pstax-navy/40 via-transparent to-transparent" />
              
              <div className="absolute bottom-8 left-8 right-8 p-6 glass rounded-2xl border-white/20">
                <p className="text-pstax-navy font-display font-bold text-lg">Pascal Schraml</p>
                <p className="text-pstax-navy/60 text-sm font-medium">{ht.role}</p>
              </div>
            </div>
            
            {/* Floating Card */}
            <div 
              className="absolute -top-10 -right-10 hidden lg:block p-6 glass rounded-2xl border-pstax-blue/20 shadow-xl z-20"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-pstax-blue flex items-center justify-center text-pstax-navy">
                  <Zap size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-pstax-navy uppercase tracking-wider">{ht.floatingBadgeTitle}</p>
                  <p className="text-xs text-pstax-navy/60">{ht.floatingBadgeSub}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
